/**
 * Les deux langues, et comment on choisit la sienne.
 *
 * ## La détection, et pourquoi elle regarde toute la liste
 *
 * `navigator.languages` est une liste ORDONNÉE de préférences, pas une seule langue.
 * Quelqu'un qui a l'allemand en premier et le français en second doit avoir le
 * français, pas la langue par défaut — c'est exactement le cas d'un Suisse ou d'un
 * Belge, et se contenter de `navigator.language` le rate.
 *
 * ## Le repli est l'anglais
 *
 * Pour quelqu'un dont on ne parle ni la langue ni les suivantes, l'anglais est ce que
 * le plus de monde lira faute de mieux. Le jeu reste français d'esprit — le
 * vocabulaire ne se traduit pas — mais pas d'accueil.
 */

import { EN } from "./en.js";
import { FR, VOCABULAIRE_COMMUN, type Textes } from "./fr.js";

export { EN, FR, VOCABULAIRE_COMMUN };
export type { Textes };

export type Langue = "fr" | "en";

/** Les langues proposées, chacune nommée **dans sa propre langue**. */
export const LANGUES: ReadonlyArray<{ readonly code: Langue; readonly nom: string }> = [
  { code: "fr", nom: "Français" },
  { code: "en", nom: "English" },
];

const DICTIONNAIRES: Readonly<Record<Langue, Textes>> = { fr: FR, en: EN };

/** Le repli : ce que lira quelqu'un dont on ne parle pas la langue. */
export const LANGUE_PAR_DEFAUT: Langue = "en";

export function langueValide(valeur: unknown): valeur is Langue {
  return valeur === "fr" || valeur === "en";
}

export function textesDe(langue: Langue): Textes {
  return DICTIONNAIRES[langue];
}

/**
 * La langue à servir d'après les préférences du navigateur.
 *
 * On prend le **premier choix compris**, et non le premier tout court : la liste est
 * ordonnée par préférence, et s'arrêter au premier élément reviendrait à ignorer les
 * suivants pour rien.
 */
export function langueDuNavigateur(
  preferences: readonly string[] = typeof navigator === "undefined"
    ? [] : navigator.languages ?? [navigator.language],
): Langue {
  for (const brute of preferences) {
    const code = brute.toLowerCase().split("-")[0];
    if (langueValide(code)) return code;
  }
  return LANGUE_PAR_DEFAUT;
}

/**
 * Fait suivre l'attribut `lang` du document.
 *
 * Ce n'est pas un détail d'affichage : c'est lui qui décide de la voix d'un lecteur
 * d'écran, de la coupure des mots et du dictionnaire de correction. Une page qui se
 * déclare française et affiche de l'anglais se fait lire avec un accent français.
 */
export function appliquerLangueAuDocument(langue: Langue): void {
  if (typeof document !== "undefined") document.documentElement.lang = langue;
}
